import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, fmtPct, STATUT_LABELS, STATUT_COLORS, PHASE_LABELS, PHASES_ORDER } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Trash2, Search, Columns3 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import NumberInput from '../components/NumberInput';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const MISSION_LABELS = { pc: 'Limitée PC (30% — 0,178%)', elargie: 'Élargie (60% — 0,356%)', complete: 'Complète (100% — 0,5933%)' };
const ASSURANCE_RATES = { pc: 0.00178, elargie: 0.00356, complete: 0.005933 };
const STATUT_ORDER = ['faisa_cours', 'faisa_ko', 'faisa_accord_probable', 'projet_cours', 'pc_depose', 'pc_accorde'];

const defaultJalons = [
  { libelle: 'Dépôt PC', pourcentage: 70, statut: 'attente' },
  { libelle: 'Obtention arrêté', pourcentage: 25, statut: 'attente' },
  { libelle: 'Purge administrative', pourcentage: 5, statut: 'attente' },
];

const emptyForm = {
  code: '', promoteur_id: '', nom: '', description: '', statut: 'faisa_cours',
  date_reception_faisa: '', date_rendu_faisa: '', date_ok_client_pc: '', date_rendu_pc: '',
  adresse_terrain: '', section_cadastrale: '', parcelles: '',
  superficie_terrain: 0, surface_plancher: 0,
  montant_travaux: 0, taux_honoraires: 1.0, honoraires_prevus: 0,
  type_mission: 'pc',
  annee_realisation: new Date().getFullYear(),
  remu_dirig_mode: 'aucun', remu_dirig_taux: 0, remu_dirig_montant_manuel: 0,
  annee_debut_travaux: new Date().getFullYear(), duree_travaux_annees: 1,
  jalons: defaultJalons,
  phase_actuelle: '',
  budget_pointages_a_venir: 0,
};

const REMU_LABELS = {
  aucun: 'Aucune',
  pct_ca: '% du CA (Honoraires × taux)',
  pct_ca_apres_assurance: '% du CA après assurance ((Honoraires – Assurance) × taux)',
  manuel: 'Montant manuel',
};

export default function Projets() {
  const [projets, setProjets] = useState([]);
  const [promoteurs, setPromoteurs] = useState([]);
  const [rent, setRent] = useState([]);
  const [filterPromo, setFilterPromo] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterAnnee, setFilterAnnee] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [groupByStatut, setGroupByStatut] = useState(false);
  const [viewMode, setViewMode] = useState('full'); // 'full' = admin/analyse | 'compact' = dépenses regroupées
  // Colonnes masquables (les autres = code, nom, actions sont toujours visibles)
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('projets_hidden_cols') || '[]')); } catch { return new Set(); }
  });
  const toggleCol = (k) => setHiddenCols(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    localStorage.setItem('projets_hidden_cols', JSON.stringify([...next]));
    return next;
  });
  const isVisible = (k) => !hiddenCols.has(k);
  // Définition des colonnes masquables
  const columnDefs = useMemo(() => {
    const base = [
      { k: 'promoteur_code', label: 'Promo' },
      { k: 'statut', label: 'Statut' },
      { k: 'montant_travaux', label: 'Travaux' },
      { k: 'honoraires', label: 'Honoraires' },
      { k: 'assurance', label: 'Assurance' },
    ];
    if (viewMode === 'full') {
      base.push({ k: 'cout', label: 'Pointages réalisés' });
      base.push({ k: 'budget_futur', label: 'Pointages à venir' });
      base.push({ k: 'remu', label: 'Rémun. dirigeants' });
    } else {
      base.push({ k: 'cout', label: 'Dépenses' });
    }
    base.push({ k: 'marge', label: 'Marge' });
    return base;
  }, [viewMode]);
  const [sort, toggleSort] = useSort('code', 'desc');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [p, r, pr] = await Promise.all([
      api.get('/projets'), api.get('/dashboard/rentabilite-projets'), api.get('/promoteurs'),
    ]);
    setProjets(p.data);
    setRent(r.data);
    setPromoteurs(pr.data);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ ...emptyForm, jalons: [...defaultJalons] }); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...emptyForm, ...p, promoteur_id: p.promoteur_id || '',
      date_reception_faisa: p.date_reception_faisa || '', date_rendu_faisa: p.date_rendu_faisa || '',
      date_ok_client_pc: p.date_ok_client_pc || '', date_rendu_pc: p.date_rendu_pc || '',
      adresse_terrain: p.adresse_terrain || '', section_cadastrale: p.section_cadastrale || '',
      parcelles: p.parcelles || '',
      montant_travaux: p.montant_travaux || 0, taux_honoraires: p.taux_honoraires || 1.0,
      honoraires_prevus: p.honoraires_prevus || 0, type_mission: p.type_mission || 'pc',
      annee_realisation: p.annee_realisation || (p.created_at ? Number(String(p.created_at).slice(0, 4)) : new Date().getFullYear()),
      remu_dirig_mode: p.remu_dirig_mode || 'aucun',
      remu_dirig_taux: p.remu_dirig_taux || 0,
      remu_dirig_montant_manuel: p.remu_dirig_montant_manuel || 0,
      annee_debut_travaux: p.annee_debut_travaux || new Date().getFullYear(),
      duree_travaux_annees: p.duree_travaux_annees || 1,
      jalons: (p.jalons && p.jalons.length) ? p.jalons : [...defaultJalons],
      phase_actuelle: p.phase_actuelle || '',
      budget_pointages_a_venir: p.budget_pointages_a_venir || 0,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const autoCode = async (promoId) => {
    if (!promoId) return;
    const promo = promoteurs.find(p => p.id === promoId);
    if (!promo) return;
    const year = new Date().getFullYear();
    const { data } = await api.get(`/projets/next-code/${promo.code}?annee=${year}`);
    setForm(f => ({ ...f, code: data.code }));
  };

  const updateJalon = (i, field, val) => {
    const j = [...form.jalons];
    j[i] = { ...j[i], [field]: field === 'pourcentage' ? Number(val) : val };
    setForm({ ...form, jalons: j });
  };
  const addJalon = () => setForm({ ...form, jalons: [...form.jalons, { libelle: '', pourcentage: 0, statut: 'attente' }] });
  const rmJalon = (i) => setForm({ ...form, jalons: form.jalons.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form, promoteur_id: form.promoteur_id || null,
        montant_travaux: Number(form.montant_travaux),
        taux_honoraires: Number(form.taux_honoraires),
        honoraires_prevus: Number(form.honoraires_prevus),
        budget_pointages_a_venir: Number(form.budget_pointages_a_venir) || 0,
        superficie_terrain: Number(form.superficie_terrain) || null,
        surface_plancher: Number(form.surface_plancher) || null,
        annee_realisation: form.annee_realisation ? Number(form.annee_realisation) : null,
        annee_debut_travaux: form.annee_debut_travaux ? Number(form.annee_debut_travaux) : null,
        duree_travaux_annees: Number(form.duree_travaux_annees),
        remu_dirig_mode: form.remu_dirig_mode || 'aucun',
        remu_dirig_taux: Number(form.remu_dirig_taux) || 0,
        remu_dirig_montant_manuel: form.remu_dirig_mode === 'manuel' ? Number(form.remu_dirig_montant_manuel) || 0 : null,
        date_reception_faisa: form.date_reception_faisa || null,
        date_rendu_faisa: form.date_rendu_faisa || null,
        date_ok_client_pc: form.date_ok_client_pc || null,
        date_rendu_pc: form.date_rendu_pc || null,
      };
      if (editingId) { await api.put(`/projets/${editingId}`, payload); toast.success('Mis à jour'); }
      else { await api.post('/projets', payload); toast.success('Créé'); }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    await api.delete(`/projets/${id}`); toast.success('Supprimé'); load();
  };

  const rentMap = useMemo(() => Object.fromEntries(rent.map(r => [r.id, r])), [rent]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return projets.filter(p => {
      if (filterPromo !== 'all' && p.promoteur_id !== filterPromo) return false;
      if (filterStatut !== 'all' && p.statut !== filterStatut) return false;
      if (filterAnnee !== 'all') {
        const ar = p.annee_realisation || (p.created_at ? Number(String(p.created_at).slice(0, 4)) : null);
        if (String(ar || '') !== String(filterAnnee)) return false;
      }
      if (s && !(p.code.toLowerCase().includes(s) || p.nom.toLowerCase().includes(s) || (p.promoteur_code || '').toLowerCase().includes(s))) return false;
      return true;
    });
  }, [projets, filterPromo, filterStatut, filterAnnee, search]);

  const anneesDispo = useMemo(() => {
    const set = new Set();
    for (const p of projets) {
      const ar = p.annee_realisation || (p.created_at ? Number(String(p.created_at).slice(0, 4)) : null);
      if (ar) set.add(ar);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [projets]);

  const sorted = useMemo(() => sortArr(filtered, sort, (p, key) => {
    const r = rentMap[p.id] || {};
    switch (key) {
      case 'code': return p.code;
      case 'nom': return p.nom;
      case 'statut': return p.statut;
      case 'promoteur_code': return p.promoteur_code || '';
      case 'montant_travaux': return p.montant_travaux || 0;
      case 'honoraires': return r.honoraires_bruts || 0;
      case 'assurance': return r.assurance || 0;
      case 'cout': return r.cout_salaries || 0;
      case 'budget_futur': return r.budget_pointages_a_venir || 0;
      case 'remu': return r.remu_dirig || 0;
      case 'marge': return r.marge_nette || 0;
      default: return 0;
    }
  }), [filtered, rentMap, sort]);

  // Selection + totals
  const allChecked = sorted.length > 0 && sorted.every(p => selected[p.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(sorted.map(p => [p.id, true])));
  };
  const selectedRents = sorted.filter(p => selected[p.id]).map(p => rentMap[p.id] || {});
  const hasSelection = selectedRents.length > 0;
  const totals = (hasSelection ? selectedRents : sorted.map(p => rentMap[p.id] || {})).reduce((acc, r) => ({
    montant_travaux: acc.montant_travaux + (r.montant_travaux || 0),
    honoraires: acc.honoraires + (r.honoraires_bruts || 0),
    assurance: acc.assurance + (r.assurance || 0),
    cout: acc.cout + (r.cout_salaries || 0),
    budget_futur: acc.budget_futur + (r.budget_pointages_a_venir || 0),
    remu: acc.remu + (r.remu_dirig || 0),
    marge: acc.marge + (r.marge_nette || 0),
  }), { montant_travaux: 0, honoraires: 0, assurance: 0, cout: 0, budget_futur: 0, remu: 0, marge: 0 });

  const honorairesCalc = Number(form.honoraires_prevus) > 0
    ? Number(form.honoraires_prevus)
    : Number(form.montant_travaux) * Number(form.taux_honoraires) / 100;

  const assuranceCalc = Number(form.montant_travaux) * (ASSURANCE_RATES[form.type_mission] || 0.00178);
  const remuDirigCalc = useMemo(() => {
    const m = form.remu_dirig_mode;
    if (m === 'aucun') return 0;
    if (m === 'manuel') return Number(form.remu_dirig_montant_manuel) || 0;
    const taux = Number(form.remu_dirig_taux) || 0;
    if (m === 'pct_ca') return honorairesCalc * taux;
    if (m === 'pct_ca_apres_assurance') return Math.max(0, (honorairesCalc - assuranceCalc) * taux);
    return 0;
  }, [form.remu_dirig_mode, form.remu_dirig_taux, form.remu_dirig_montant_manuel, honorairesCalc, assuranceCalc]);

  const SortH = ({ k, label, align = 'left', w = 'min-w-[100px]' }) => (
    <th className={`px-0 py-0 align-top ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <div className={`resize-x overflow-hidden ${w} px-3 py-2 border-r border-zinc-100`}>
        <SortHeader sort={sort} onToggle={toggleSort} k={k} label={label} align={align} />
      </div>
    </th>
  );

  // Totals per statut group (based on all filtered, not selection)
  const groupTotals = useMemo(() => {
    const g = {};
    for (const s of STATUT_ORDER) g[s] = { count: 0, travaux: 0, honoraires: 0, assurance: 0, cout: 0, budget_futur: 0, remu: 0, marge: 0 };
    for (const p of sorted) {
      const r = rentMap[p.id] || {};
      const k = p.statut in g ? p.statut : 'projet_cours';
      g[k].count += 1;
      g[k].travaux += r.montant_travaux || 0;
      g[k].honoraires += r.honoraires_bruts || 0;
      g[k].assurance += r.assurance || 0;
      g[k].cout += r.cout_salaries || 0;
      g[k].budget_futur += r.budget_pointages_a_venir || 0;
      g[k].remu += r.remu_dirig || 0;
      g[k].marge += r.marge_nette || 0;
    }
    return g;
  }, [sorted, rentMap]);

  const renderRow = (p) => {
    const r = rentMap[p.id] || {};
    const margeColor = r.marge_nette >= 0 ? 'text-emerald-700' : 'text-rose-600';
    const depensesTotal = (r.cout_salaries || 0) + (r.budget_pointages_a_venir || 0) + (r.remu_dirig || 0);
    return (
      <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
        <td className="px-2 py-3"><Checkbox className="h-3.5 w-3.5 rounded-none" checked={!!selected[p.id]} onCheckedChange={() => setSelected(s => ({ ...s, [p.id]: !s[p.id] }))} data-testid={`select-${p.code}`} /></td>
        <td className="px-3 py-3 font-mono text-xs font-medium">{p.code}</td>
        {isVisible('promoteur_code') && <td className="px-3 py-3 font-mono text-xs text-zinc-500">{p.promoteur_code || '—'}</td>}
        <td className="px-3 py-3 font-medium">{p.nom}</td>
        {isVisible('statut') && (
          <td className="px-3 py-3">
            <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${STATUT_COLORS[p.statut]}`}>{STATUT_LABELS[p.statut]}</span>
          </td>
        )}
        {isVisible('montant_travaux') && <td className="px-3 py-3 font-mono text-right text-xs">{fmtEUR(p.montant_travaux)}</td>}
        {isVisible('honoraires') && <td className="px-3 py-3 font-mono text-right text-[#002FA7]">{fmtEUR(r.honoraires_bruts || 0)}</td>}
        {isVisible('assurance') && <td className="px-3 py-3 font-mono text-right text-amber-700 text-xs">{fmtEUR(r.assurance || 0)}</td>}
        {viewMode === 'full' ? (
          <>
            {isVisible('cout') && <td className="px-3 py-3 font-mono text-right text-zinc-700 text-xs">{fmtEUR(r.cout_salaries || 0)}</td>}
            {isVisible('budget_futur') && <td className="px-3 py-3 font-mono text-right text-orange-700 text-xs">{fmtEUR(r.budget_pointages_a_venir || 0)}</td>}
            {isVisible('remu') && <td className="px-3 py-3 font-mono text-right text-violet-700 text-xs">{fmtEUR(r.remu_dirig || 0)}</td>}
          </>
        ) : (
          isVisible('cout') && <td className="px-3 py-3 font-mono text-right text-zinc-700 text-xs">{fmtEUR(depensesTotal)}</td>
        )}
        {isVisible('marge') && <td className={`px-3 py-3 font-mono text-right font-medium ${margeColor}`}>{fmtEUR(r.marge_nette || 0)}</td>}
        <td className="px-3 py-3 text-right">
          <div className="flex gap-1 justify-end">
            <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-zinc-200"><Pencil size={13} /></button>
            <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-rose-100 text-rose-600"><Trash2 size={13} /></button>
          </div>
        </td>
      </tr>
    );
  };

  // Nb total de colonnes pour colspan (utile dans les rangées de groupe)
  const colSpanAll = useMemo(() => {
    let n = 3; // checkbox + code + nom
    if (isVisible('promoteur_code')) n++;
    if (isVisible('statut')) n++;
    for (const k of ['montant_travaux', 'honoraires', 'assurance', 'marge']) if (isVisible(k)) n++;
    if (viewMode === 'full') {
      for (const k of ['cout', 'budget_futur', 'remu']) if (isVisible(k)) n++;
    } else {
      if (isVisible('cout')) n++;
    }
    n++; // actions
    return n;
  }, [hiddenCols, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout
      title="Projets"
      subtitle={`${sorted.length} / ${projets.length} projets${hasSelection ? ` — ${selectedRents.length} sélectionnés` : ''}`}
      actions={
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button onClick={openCreate} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="nouveau-projet-btn">
              <Plus size={16} className="mr-2" /> Nouveau projet
            </Button>
          </SheetTrigger>
          <SheetContent className="rounded-none w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display font-bold text-2xl tracking-tight">{editingId ? 'Modifier' : 'Nouveau projet'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="projet-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Promoteur *</Label>
                  <Select value={form.promoteur_id} onValueChange={(v) => { setForm({ ...form, promoteur_id: v, code: '' }); if (!editingId) autoCode(v); }}>
                    <SelectTrigger className="rounded-none" data-testid="projet-promoteur-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {promoteurs.map((p) => <SelectItem key={p.id} value={p.id}><span className="font-mono">{p.code}</span> — {p.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Code (auto)</Label>
                  <Input className="rounded-none font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FP2606" data-testid="projet-code-input" />
                </div>
              </div>
              <div>
                <Label>Nom projet *</Label>
                <Input required className="rounded-none" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="projet-nom-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Statut</Label>
                  <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {Object.entries(STATUT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type mission</Label>
                  <Select value={form.type_mission} onValueChange={(v) => setForm({ ...form, type_mission: v })}>
                    <SelectTrigger className="rounded-none" data-testid="projet-mission-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {Object.entries(MISSION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Phase courante (suivi heures)</Label>
                <Select value={form.phase_actuelle || ''} onValueChange={(v) => setForm({ ...form, phase_actuelle: v })}>
                  <SelectTrigger className="rounded-none" data-testid="projet-phase-actuelle-select">
                    <SelectValue placeholder="— Non renseignée —" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PHASES_ORDER.map(p => <SelectItem key={p} value={p}>{PHASE_LABELS[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-500 mt-1">Sert au reporting Analyse temporelle et à la qualification des heures à risque.</p>
              </div>

              <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Phase faisabilité</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date réception</Label><Input type="date" className="rounded-none font-mono h-9" value={form.date_reception_faisa} onChange={(e) => setForm({ ...form, date_reception_faisa: e.target.value })} /></div>
                  <div><Label className="text-xs">Date rendu</Label><Input type="date" className="rounded-none font-mono h-9" value={form.date_rendu_faisa} onChange={(e) => setForm({ ...form, date_rendu_faisa: e.target.value })} /></div>
                </div>
              </div>

              <div className="bg-blue-50 border border-[#002FA7]/30 p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-medium">Phase projet / PC</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date OK client</Label><Input type="date" className="rounded-none font-mono h-9" value={form.date_ok_client_pc} onChange={(e) => setForm({ ...form, date_ok_client_pc: e.target.value })} /></div>
                  <div><Label className="text-xs">Date rendu PC</Label><Input type="date" className="rounded-none font-mono h-9" value={form.date_rendu_pc} onChange={(e) => setForm({ ...form, date_rendu_pc: e.target.value })} /></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Montant travaux (€)</Label><Input type="number" className="rounded-none font-mono" value={form.montant_travaux} onChange={(e) => setForm({ ...form, montant_travaux: e.target.value })} /></div>
                <div><Label>Taux honoraires (%)</Label><Input type="number" step="0.01" className="rounded-none font-mono" value={form.taux_honoraires} onChange={(e) => setForm({ ...form, taux_honoraires: e.target.value })} /></div>
              </div>
              <div><Label>Ou honoraires forfaitaires (€)</Label><Input type="number" className="rounded-none font-mono" value={form.honoraires_prevus} onChange={(e) => setForm({ ...form, honoraires_prevus: e.target.value })} /></div>
              <div>
                <Label>Budget pointages à venir (€)</Label>
                <Input type="number" min="0" step="100" className="rounded-none font-mono" value={form.budget_pointages_a_venir} onChange={(e) => setForm({ ...form, budget_pointages_a_venir: e.target.value })} placeholder="0" data-testid="projet-budget-pointages-input" />
                <p className="text-[10px] text-zinc-500 mt-1">Estimation des heures salariés restantes à consommer (utile pour anticiper la marge finale).</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Année réalisation</Label><Input type="number" className="rounded-none font-mono" value={form.annee_realisation || ''} onChange={(e) => setForm({ ...form, annee_realisation: e.target.value })} placeholder="2026" data-testid="projet-annee-realisation" /></div>
                <div><Label>Début travaux</Label><Input type="number" className="rounded-none font-mono" value={form.annee_debut_travaux} onChange={(e) => setForm({ ...form, annee_debut_travaux: e.target.value })} /></div>
              </div>
              <div><Label>Durée (ans)</Label><Input type="number" min="1" max="4" className="rounded-none font-mono w-32" value={form.duree_travaux_annees} onChange={(e) => setForm({ ...form, duree_travaux_annees: e.target.value })} /></div>

              {/* === Rémunération dirigeants === */}
              <RemuDirigSection form={form} setForm={setForm}
                honoraires={honorairesCalc} assurance={assuranceCalc} promoteurId={form.promoteur_id} />

              <div className="border border-zinc-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Jalons facturation</div>
                  <button type="button" onClick={addJalon} className="text-xs text-[#002FA7] hover:underline">+ Ajouter</button>
                </div>
                {form.jalons.map((j, i) => (
                  <div key={j.id || `jalon-${i}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-7"><Label className="text-[10px]">Libellé</Label><Input className="rounded-none h-9" value={j.libelle} onChange={(e) => updateJalon(i, 'libelle', e.target.value)} /></div>
                    <div className="col-span-3"><Label className="text-[10px]">%</Label><Input type="number" className="rounded-none h-9 font-mono" value={j.pourcentage} onChange={(e) => updateJalon(i, 'pourcentage', e.target.value)} /></div>
                    <div className="col-span-2 font-mono text-xs text-right pb-2">{fmtEUR(honorairesCalc * j.pourcentage / 100)}</div>
                    <div className="col-span-12 flex justify-end"><button type="button" onClick={() => rmJalon(i)} className="text-xs text-rose-600">Supprimer</button></div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-1">
                <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Calculs auto</div>
                <div className="flex justify-between text-sm font-mono"><span>Honoraires bruts</span><span className="font-medium">{fmtEUR(honorairesCalc)}</span></div>
                <div className="flex justify-between text-sm font-mono"><span>Assurance ({((ASSURANCE_RATES[form.type_mission] || 0.00178) * 100).toFixed(4)}%)</span><span className="font-medium text-amber-700">{fmtEUR(assuranceCalc)}</span></div>
                <div className="flex justify-between text-sm font-mono"><span>Rémunération dirigeants</span><span className="font-medium text-violet-700">{fmtEUR(remuDirigCalc)}</span></div>
                <div className="flex justify-between text-sm font-mono pt-1 mt-1 border-t border-zinc-300"><span className="font-semibold">Marge prévisionnelle</span><span className="font-semibold text-emerald-700">{fmtEUR(honorairesCalc - assuranceCalc - remuDirigCalc)}</span></div>
                <div className="text-[10px] text-zinc-500 italic mt-1">Marge avant déduction du coût des heures pointées.</div>
              </div>

              <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="projet-submit-btn">{editingId ? 'Mettre à jour' : 'Créer'}</Button>
            </form>
          </SheetContent>
        </Sheet>
      }
    >
      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Recherche</Label>
          <Search size={14} className="absolute left-3 top-[34px] text-zinc-400" />
          <Input className="rounded-none pl-9" placeholder="Code, nom, promoteur…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-projets" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Promoteur</Label>
          <Select value={filterPromo} onValueChange={setFilterPromo}>
            <SelectTrigger className="rounded-none w-48" data-testid="filter-promoteur"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous</SelectItem>
              {promoteurs.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Année réalisation</Label>
          <Select value={filterAnnee} onValueChange={setFilterAnnee}>
            <SelectTrigger className="rounded-none w-32" data-testid="filter-annee"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Toutes</SelectItem>
              {anneesDispo.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Statut</Label>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="rounded-none w-56" data-testid="filter-statut"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous</SelectItem>
              {STATUT_ORDER.map((k) => <SelectItem key={k} value={k}>{STATUT_LABELS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-none h-10 border-zinc-900" data-testid="toggle-columns-btn">
                <Columns3 size={14} className="mr-2" /> Colonnes
                {hiddenCols.size > 0 && <span className="ml-2 text-[10px] font-mono bg-amber-100 text-amber-800 px-1.5 py-0.5">{hiddenCols.size}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="rounded-none w-64 p-0" align="end">
              <div className="border-b border-zinc-200 px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Afficher / masquer</span>
                <button onClick={() => { setHiddenCols(new Set()); localStorage.setItem('projets_hidden_cols', '[]'); }} className="text-xs text-zinc-600 hover:text-zinc-900 underline">Tout afficher</button>
              </div>
              {columnDefs.map(c => (
                <button
                  key={c.k}
                  onClick={() => toggleCol(c.k)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-zinc-50"
                  data-testid={`col-toggle-${c.k}`}
                >
                  <span className={`w-4 h-4 border ${isVisible(c.k) ? 'bg-[#002FA7] border-[#002FA7]' : 'border-zinc-400'} flex items-center justify-center`}>
                    {isVisible(c.k) && <span className="text-white text-[10px]">✓</span>}
                  </span>
                  <span>{c.label}</span>
                </button>
              ))}
              <div className="border-t border-zinc-100 px-3 py-2 text-[10px] text-zinc-500">
                Code, Nom et actions restent toujours visibles.
              </div>
            </PopoverContent>
          </Popover>
          <Button variant={viewMode === 'compact' ? 'default' : 'outline'}
            className={`rounded-none h-10 ${viewMode === 'compact' ? 'bg-zinc-900 hover:bg-zinc-800' : 'border-zinc-900'}`}
            onClick={() => setViewMode(v => v === 'full' ? 'compact' : 'full')} data-testid="toggle-view-mode"
            title={viewMode === 'full' ? 'Passer en vue compacte (Dépenses regroupées)' : 'Passer en vue Admin / Analyse (colonnes détaillées)'}>
            {viewMode === 'full' ? 'Vue Admin' : 'Vue Compacte'}
          </Button>
          <Button variant={groupByStatut ? 'default' : 'outline'}
            className={`rounded-none h-10 ${groupByStatut ? 'bg-[#002FA7] hover:bg-[#001B63]' : 'border-zinc-900'}`}
            onClick={() => setGroupByStatut(g => !g)} data-testid="toggle-group-statut">
            {groupByStatut ? 'Vue plate' : 'Grouper par statut'}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 max-h-[calc(100vh-260px)] overflow-auto" data-testid="projets-table">
        <table className="w-full text-sm" style={{ tableLayout: 'auto' }}>
          <thead className="sticky top-0 z-20">
            <tr className="bg-zinc-100 border-b border-zinc-300 shadow-sm">
              <td className="px-2 py-3 w-6"></td>
              <td colSpan={2 + (isVisible('promoteur_code') ? 1 : 0) + (isVisible('statut') ? 1 : 0)} className="px-3 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-900 font-semibold">
                {hasSelection ? `Total sélection (${selectedRents.length})` : `Total (${sorted.length})`}
              </td>
              {isVisible('montant_travaux') && <td className="px-3 py-3 font-mono text-right text-xs font-semibold">{fmtEUR(totals.montant_travaux)}</td>}
              {isVisible('honoraires') && <td className="px-3 py-3 font-mono text-right text-[#002FA7] font-semibold">{fmtEUR(totals.honoraires)}</td>}
              {isVisible('assurance') && <td className="px-3 py-3 font-mono text-right text-amber-700 font-semibold">{fmtEUR(totals.assurance)}</td>}
              {viewMode === 'full' ? (
                <>
                  {isVisible('cout') && <td className="px-3 py-3 font-mono text-right font-semibold">{fmtEUR(totals.cout)}</td>}
                  {isVisible('budget_futur') && <td className="px-3 py-3 font-mono text-right text-orange-700 font-semibold">{fmtEUR(totals.budget_futur)}</td>}
                  {isVisible('remu') && <td className="px-3 py-3 font-mono text-right text-violet-700 font-semibold">{fmtEUR(totals.remu)}</td>}
                </>
              ) : (
                isVisible('cout') && <td className="px-3 py-3 font-mono text-right font-semibold">{fmtEUR(totals.cout + totals.budget_futur + totals.remu)}</td>
              )}
              {isVisible('marge') && <td className={`px-3 py-3 font-mono text-right font-semibold ${totals.marge >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(totals.marge)}</td>}
              <td></td>
            </tr>
            <tr className="text-left bg-white border-b border-zinc-900">
              <th className="px-2 py-2 w-6">
                <Checkbox className="h-3.5 w-3.5 rounded-none" checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" />
              </th>
              <SortH k="code" label="Code" w="min-w-[80px]" />
              {isVisible('promoteur_code') && <SortH k="promoteur_code" label="Promo" w="min-w-[70px]" />}
              <SortH k="nom" label="Nom" w="min-w-[180px]" />
              {isVisible('statut') && <SortH k="statut" label="Statut" w="min-w-[150px]" />}
              {isVisible('montant_travaux') && <SortH k="montant_travaux" label="Travaux" align="right" w="min-w-[100px]" />}
              {isVisible('honoraires') && <SortH k="honoraires" label="Honoraires" align="right" w="min-w-[100px]" />}
              {isVisible('assurance') && <SortH k="assurance" label="Assurance" align="right" w="min-w-[90px]" />}
              {viewMode === 'full' ? (
                <>
                  {isVisible('cout') && <SortH k="cout" label="Pointages réalisés" align="right" w="min-w-[110px]" />}
                  {isVisible('budget_futur') && <SortH k="budget_futur" label="Pointages à venir" align="right" w="min-w-[110px]" />}
                  {isVisible('remu') && <SortH k="remu" label="Rémun. dirig." align="right" w="min-w-[100px]" />}
                </>
              ) : (
                isVisible('cout') && <SortH k="cout" label="Dépenses" align="right" w="min-w-[120px]" />
              )}
              {isVisible('marge') && <SortH k="marge" label="Marge" align="right" w="min-w-[100px]" />}
              <th className="w-[70px]"></th>
            </tr>
          </thead>
          {groupByStatut ? (
            STATUT_ORDER.map((st) => {
              const rows = sorted.filter(p => p.statut === st);
              if (rows.length === 0) return null;
              const gt = groupTotals[st];
              return (
                <tbody key={st}>
                  <tr className="bg-zinc-50 border-y border-zinc-300">
                    <td className="px-2 py-2 w-6"></td>
                    <td colSpan={2 + (isVisible('promoteur_code') ? 1 : 0) + (isVisible('statut') ? 1 : 0)} className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${STATUT_COLORS[st]}`}>
                        {STATUT_LABELS[st]} — {gt.count}
                      </span>
                    </td>
                    {isVisible('montant_travaux') && <td className="px-3 py-2 font-mono text-right text-xs font-semibold">{fmtEUR(gt.travaux)}</td>}
                    {isVisible('honoraires') && <td className="px-3 py-2 font-mono text-right text-[#002FA7] font-semibold">{fmtEUR(gt.honoraires)}</td>}
                    {isVisible('assurance') && <td className="px-3 py-2 font-mono text-right text-amber-700 font-semibold">{fmtEUR(gt.assurance)}</td>}
                    {viewMode === 'full' ? (
                      <>
                        {isVisible('cout') && <td className="px-3 py-2 font-mono text-right font-semibold">{fmtEUR(gt.cout)}</td>}
                        {isVisible('budget_futur') && <td className="px-3 py-2 font-mono text-right text-orange-700 font-semibold">{fmtEUR(gt.budget_futur)}</td>}
                        {isVisible('remu') && <td className="px-3 py-2 font-mono text-right text-violet-700 font-semibold">{fmtEUR(gt.remu)}</td>}
                      </>
                    ) : (
                      isVisible('cout') && <td className="px-3 py-2 font-mono text-right font-semibold">{fmtEUR(gt.cout + gt.budget_futur + gt.remu)}</td>
                    )}
                    {isVisible('marge') && <td className={`px-3 py-2 font-mono text-right font-semibold ${gt.marge >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(gt.marge)}</td>}
                    <td></td>
                  </tr>
                  {rows.map(renderRow)}
                </tbody>
              );
            })
          ) : (
            <tbody>
              {sorted.map(renderRow)}
              {sorted.length === 0 && (
                <tr><td colSpan={colSpanAll} className="px-4 py-12 text-center text-zinc-500">Aucun projet.</td></tr>
              )}
            </tbody>
          )}
        </table>
      </div>
    </Layout>
  );
}

function RemuDirigSection({ form, setForm, honoraires, assurance, promoteurId }) {
  const [suggestion, setSuggestion] = React.useState(null);
  React.useEffect(() => {
    if (!promoteurId) { setSuggestion(null); return; }
    api.get(`/admin/remu-dirig-suggestion?promoteur_id=${promoteurId}`).then(r => setSuggestion(r.data)).catch(() => setSuggestion(null));
  }, [promoteurId]);

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm(f => ({
      ...f,
      remu_dirig_mode: suggestion.mode_recommande || 'pct_ca_apres_assurance',
      remu_dirig_taux: suggestion.taux_moyen || 0.30,
      remu_dirig_montant_manuel: suggestion.montant_moyen || 0,
    }));
  };

  const tauxPct = (Number(form.remu_dirig_taux) || 0) * 100;
  const updateTauxPct = (v) => setForm(f => ({ ...f, remu_dirig_taux: (Number(v) || 0) / 100 }));

  return (
    <div className="bg-violet-50/40 border border-violet-200 p-4 space-y-3" data-testid="remu-dirig-section">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-violet-800 font-medium">Rémunération dirigeants</div>
        {suggestion && suggestion.nb_projets_sample > 0 && (
          <button type="button" onClick={applySuggestion} className="text-[10px] text-violet-700 hover:underline" data-testid="apply-suggestion">
            Suggéré ({suggestion.nb_projets_sample} proj.) : {fmtEUR(suggestion.montant_moyen || 0)} →
          </button>
        )}
      </div>
      <div>
        <Label className="text-xs">Mode</Label>
        <Select value={form.remu_dirig_mode} onValueChange={(v) => setForm({ ...form, remu_dirig_mode: v })}>
          <SelectTrigger className="rounded-none" data-testid="remu-dirig-mode"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {Object.entries(REMU_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {(form.remu_dirig_mode === 'pct_ca' || form.remu_dirig_mode === 'pct_ca_apres_assurance') && (
        <div>
          <Label className="text-xs">Taux (%)</Label>
          <Input type="number" step="0.5" className="rounded-none font-mono" value={tauxPct} onChange={(e) => updateTauxPct(e.target.value)} data-testid="remu-dirig-taux" />
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {form.remu_dirig_mode === 'pct_ca'
              ? `${fmtEUR(honoraires)} × ${tauxPct.toFixed(1)}% = `
              : `(${fmtEUR(honoraires)} − ${fmtEUR(assurance)}) × ${tauxPct.toFixed(1)}% = `}
            <strong className="text-violet-700">
              {fmtEUR(form.remu_dirig_mode === 'pct_ca'
                ? honoraires * (tauxPct / 100)
                : Math.max(0, (honoraires - assurance) * (tauxPct / 100)))}
            </strong>
          </div>
        </div>
      )}
      {form.remu_dirig_mode === 'manuel' && (
        <div>
          <Label className="text-xs">Montant (€)</Label>
          <Input type="number" className="rounded-none font-mono" value={form.remu_dirig_montant_manuel || 0} onChange={(e) => setForm({ ...form, remu_dirig_montant_manuel: e.target.value })} data-testid="remu-dirig-manuel" />
        </div>
      )}
      {suggestion && suggestion.nb_projets_sample === 0 && (
        <div className="text-[10px] text-zinc-500 italic">
          Pas d&apos;historique pour ce promoteur. Valeurs par défaut applicables (Paramètres : {REMU_LABELS[suggestion.mode_recommande] || '—'} à {((suggestion.taux_moyen || 0) * 100).toFixed(1)}%).
        </div>
      )}
    </div>
  );
}
